import type { FC, ReactNode } from 'react'

import Lenis from 'lenis'
import { useRouter } from 'next/router'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef
} from 'react'

interface SmoothScrollProps {
    children: ReactNode
}

interface ScrollToOptions {
    offset?: number
    immediate?: boolean
}

interface SmoothScrollContextValue {
    lock: () => void
    unlock: () => void
    scrollTo: (target: number | string | HTMLElement, options?: ScrollToOptions) => void
}

const SmoothScrollContext = createContext<SmoothScrollContextValue>({
    lock: () => undefined,
    unlock: () => undefined,
    scrollTo: () => undefined
})

export const useSmoothScroll = (): SmoothScrollContextValue => useContext(SmoothScrollContext)

export const SmoothScroll: FC<SmoothScrollProps> = ({ children }): ReactNode => {
    const lenisRef = useRef<Lenis | null>(null)
    const router = useRouter()

    useEffect(() => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (reduceMotion) return

        const lenis = new Lenis({
            autoRaf: true,
            lerp: 0.1,
            smoothWheel: true,
            stopInertiaOnNavigate: true
        })

        lenisRef.current = lenis

        return () => {
            lenis.destroy()
            lenisRef.current = null
        }
    }, [])

    useEffect(() => {
        const handleRouteDone = (): void => {
            lenisRef.current?.resize()
        }

        router.events.on('routeChangeComplete', handleRouteDone)

        return () => {
            router.events.off('routeChangeComplete', handleRouteDone)
        }
    }, [router.events])

    const lock = useCallback((): void => {
        lenisRef.current?.stop()
        document.documentElement.style.overflow = 'hidden'
    }, [])

    const unlock = useCallback((): void => {
        document.documentElement.style.overflow = ''
        lenisRef.current?.start()
    }, [])

    const scrollTo = useCallback((target: number | string | HTMLElement, options: ScrollToOptions = {}): void => {
        const { offset = 0, immediate = false } = options

        if (lenisRef.current) {
            lenisRef.current.scrollTo(target, { offset, immediate, duration: 1.4 })
            return
        }

        if (typeof target === 'number') {
            window.scrollTo({ top: target + offset, behavior: immediate ? 'auto' : 'smooth' })
            return
        }

        const element = typeof target === 'string' ? document.querySelector(target) : target

        if (!(element instanceof HTMLElement)) return

        const top = element.getBoundingClientRect().top + window.scrollY + offset

        window.scrollTo({ top, behavior: immediate ? 'auto' : 'smooth' })
    }, [])

    const value = useMemo(() => ({ lock, unlock, scrollTo }), [lock, unlock, scrollTo])

    return (
        <SmoothScrollContext.Provider value={value}>
            {children}
        </SmoothScrollContext.Provider>
    )
}
